import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface TextPromptDialogData {
  pretext?: string;
  /** Rendered after `pretext` as HTML, for the rare prompt that needs emphasis - a phrase the user
   *  must type back, say. Pass only text you built yourself; anything user-supplied belongs in the
   *  interpolated `pretext` above. */
  pretextHtml?: string;
  posttext?: string;
  text: string;
  /** Type-to-confirm. When set, Ok stays disabled until the typed value matches this phrase under
   *  normalizePromptPhrase. Leave unset for an ordinary prompt, where Ok is always available. */
  requiredPhrase?: string;
}

/**
 * The comparison behind `requiredPhrase`, exported so a caller can apply the same rule to whatever
 * the dialog hands back rather than writing a second, drifting copy of it.
 *
 * Deliberately forgiving: wrapping quotes (straight or curly - Windows and Office produce curly
 * ones) are stripped, whitespace is collapsed and case is ignored. A type-to-confirm prompt exists
 * to prove intent, not typing accuracy, and somebody shown a quoted phrase will copy the quotes.
 */
export function normalizePromptPhrase(s: string): string {
  return s
    .trim()
    .replace(/^["'“”‘’]+|["'“”‘’]+$/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

@Component({
    selector: 'prox-textprompt-dialog',
    template: `
  <div>
    <div class="text-l mb-3">{{ data.pretext }}</div>
    <div *ngIf="data.pretextHtml" class="text-l mb-3" [innerHTML]="data.pretextHtml"></div>
    <input type="text" autofocus pInputText [(ngModel)]="value" class="w-full" />
    <div class="text-l mb-3">{{ data.posttext}}</div>
    <div class="text-right mt-3">
      <button (click)="ok()" pButton type="button" icon="pi pi-check" label="Ok"
        [disabled]="!canOk()" class="p-button-raised p-button-primary"></button>
      <button (click)="cancel()" pButton type="button" icon="pi pi-times" label="Cancel"
        class="p-button-raised p-button-primary ml-2"></button>
    </div>
  </div>
  `,
    standalone: false
})
export class TextPromptDialogComponent {

  data: TextPromptDialogData;
  value: string;

  constructor(dialogConfig: DynamicDialogConfig<TextPromptDialogData>, private ref: DynamicDialogRef) {
    this.data = dialogConfig.data || { text: '' };
    this.value = dialogConfig.data?.text || '';
  }

  /** True for an ordinary prompt; for a type-to-confirm one, only once the phrase matches. */
  canOk(): boolean {
    if (!this.data.requiredPhrase) return true;
    return normalizePromptPhrase(this.value ?? '') === normalizePromptPhrase(this.data.requiredPhrase);
  }

  ok() {
    if (!this.canOk()) return;
    this.ref.close(this.value);
  }
  cancel() {
    this.ref.close();
  }

}
